/**
 * Preview Panel Component
 * 
 * Test targeting rules with sample context
 */

import { useState, useCallback } from "react";
import {
  Card,
  BlockStack,
  InlineStack,
  Button,
  Text,
  TextField,
  Banner,
  Badge,
} from "@shopify/polaris";
import type { TargetingRuleV1, EvaluationContext, EvaluationResult } from "./types";
import { previewTargetingRule } from "../../api/campaigns";

interface PreviewPanelProps {
  rule: TargetingRuleV1;
  onPreview?: (rule: TargetingRuleV1, context: EvaluationContext) => Promise<EvaluationResult>;
}

export function PreviewPanel({ rule, onPreview }: PreviewPanelProps) {
  const [context, setContext] = useState<string>(JSON.stringify(DEFAULT_CONTEXT, null, 2));
  const [result, setResult] = useState<EvaluationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);

  /**
   * Handle preview
   */
  const handlePreview = useCallback(async () => {
    setError(null);
    setResult(null);
    setIsPreviewing(true);

    try {
      // Parse context
      let parsedContext: EvaluationContext;
      try {
        parsedContext = JSON.parse(context);
      } catch (parseError) {
        throw new Error("Invalid JSON in context");
      }

      // Use custom preview function if provided, otherwise use API
      if (onPreview) {
        const previewResult = await onPreview(rule, parsedContext);
        setResult(previewResult);
      } else {
        // Call admin API via centralized client
        const data = await previewTargetingRule(rule);
        setResult(data.result);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Preview failed");
    } finally {
      setIsPreviewing(false);
    }
  }, [context, rule, onPreview]);

  /**
   * Reset context to default
   */
  const handleResetContext = useCallback(() => {
    setContext(JSON.stringify(DEFAULT_CONTEXT, null, 2));
    setResult(null);
    setError(null);
  }, []);

  return (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          Preview & Test
        </Text>

        <Text as="p" tone="subdued">
          Test your targeting rules with sample customer, cart, product, and geo data.
        </Text>

        <TextField
          label="Sample Context (JSON)"
          value={context}
          onChange={setContext}
          multiline={10}
          autoComplete="off"
          helpText="Edit the JSON to test different scenarios"
        />

        <InlineStack gap="200">
          <Button onClick={handlePreview} loading={isPreviewing}>
            Test Rules
          </Button>
          <Button onClick={handleResetContext} variant="plain">
            Reset to Default
          </Button>
        </InlineStack>

        {error && (
          <Banner tone="critical">
            <Text as="p">{error}</Text>
          </Banner>
        )}

        {result && (
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h3" variant="headingSm">
                  Result
                </Text>
                <Badge tone={result.matched ? "success" : "attention"}>
                  {result.matched ? "MATCHED" : "NOT MATCHED"}
                </Badge>
              </InlineStack>

              <BlockStack gap="200">
                <Text as="p" variant="bodySm">
                  <strong>Explanation:</strong> {result.explanation}
                </Text>

                <InlineStack gap="400">
                  <Text as="p" variant="bodySm" tone="subdued">
                    Evaluated: {result.evaluatedConditions} condition(s)
                  </Text>
                  {result.shortCircuited && (
                    <Badge tone="info">Short-circuited</Badge>
                  )}
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        )}

        <Banner tone="info">
          <BlockStack gap="100">
            <Text as="p" variant="bodySm">
              <strong>Tip:</strong> Modify the context JSON to test different scenarios:
            </Text>
            <Text as="p" variant="bodySm">
              • Change customer.totalSpent to test spending thresholds
            </Text>
            <Text as="p" variant="bodySm">
              • Add/remove cart items to test cart conditions
            </Text>
            <Text as="p" variant="bodySm">
              • Change geo.countryCode to test geographic targeting
            </Text>
          </BlockStack>
        </Banner>
      </BlockStack>
    </Card>
  );
}

/**
 * Default evaluation context for preview
 */
const DEFAULT_CONTEXT: EvaluationContext = {
  customer: {
    id: "cust_123",
    email: "customer@example.com",
    tags: ["vip", "subscriber"],
    ordersCount: 5,
    totalSpent: 250,
    isNew: false,
  },
  cart: {
    subtotal: 75.50,
    itemsCount: 3,
    items: [
      {
        productId: "gid://shopify/Product/1234567890",
        variantId: "gid://shopify/ProductVariant/1234567890",
        collectionIds: ["gid://shopify/Collection/1234567890"],
        quantity: 2,
        price: 25.00,
      },
      {
        productId: "gid://shopify/Product/9876543210",
        variantId: "gid://shopify/ProductVariant/9876543210",
        collectionIds: [],
        quantity: 1,
        price: 25.50,
      },
    ],
  },
  product: {
    id: "gid://shopify/Product/1234567890",
    vendor: "Example Vendor",
    type: "Apparel",
    tags: ["summer", "sale"],
  },
  geo: {
    countryCode: "US",
    regionCode: "CA",
  },
  session: {
    shopId: "shop_123",
    playerId: "player_123",
    deviceId: "device_123",
    timestamp: new Date(),
  },
};
